import React from 'react';

const CARDS = [
  {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
      </svg>
    ),
    label: 'Backend & APIs',
    desc:  'Laravel, FastAPI, REST APIs, Queue Systems, WebSockets, Real-Time Systems',
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
      </svg>
    ),
    label: 'Frontend & Mobile',
    desc:  'Angular, React.js, Vue.js, React Native, Livewire, Inertia.js',
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
      </svg>
    ),
    label: 'AI & Automation',
    desc:  'ChatGPT, Gemini, AI Agents, Intelligent Extraction, LLM Workflow Automation',
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />
      </svg>
    ),
    label: 'Infrastructure & DevOps',
    desc:  'AWS, Docker, CI/CD, Linux Server Management, Git',
  },
];

export default function About() {
  return (
    <section id="about" className="py-16 lg:py-24 bg-slate-900">
      <div className="max-w-6xl mx-auto px-5 sm:px-6">

        <p className="font-mono text-sky-400 text-xs sm:text-sm tracking-widest mb-2">01. About Me</p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-100 mb-8 lg:mb-14 leading-tight">
          The engineer behind<br className="hidden sm:block" /> the product
        </h2>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">

          {/* Bio */}
          <div>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed mb-4 sm:mb-5">
              I'm <span className="text-slate-100 font-semibold">Muhammad Ilyas</span>, a
              Senior Full Stack &amp; AI Systems Engineer with{' '}
              <span className="text-sky-400 font-semibold">5+ years</span> designing and scaling
              SaaS, logistics, real-time, and AI-powered platforms.
            </p>
            <p className="text-slate-400 text-sm sm:text-base leading-relaxed mb-4 sm:mb-5">
              Proven track record of modernising legacy systems —{' '}
              <span className="text-slate-200 font-medium">cutting API response times from 20 seconds to sub-200ms</span> —
              and shipping <span className="text-slate-200 font-medium">LLM-driven automation</span> with
              ChatGPT and Gemini for document extraction, financial analysis, and intelligent workflows.
            </p>
            <p className="text-slate-400 text-sm sm:text-base leading-relaxed mb-6 lg:mb-10">
              Full-stack across Laravel, Angular, React, Vue.js, and React Native, with deep strengths
              in backend optimisation, scalable architecture, and real-time systems.
            </p>

            {/* Metadata */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6 lg:mb-10">
              {[
                { label: 'Location',     value: 'Lahore, Pakistan'       },
                { label: 'Availability', value: '30-day notice'          },
                { label: 'Education',    value: 'B.Sc. Computer Science' },
                { label: 'University',   value: 'Baltistan University'   },
              ].map(m => (
                <div key={m.label}>
                  <p className="font-mono text-[10px] sm:text-xs text-slate-600 uppercase tracking-widest mb-0.5">{m.label}</p>
                  <p className="text-slate-300 text-xs sm:text-sm font-medium">{m.value}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 sm:gap-3">
              <a
                href="https://github.com/IlyasDev123"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-slate-300 bg-slate-800 hover:bg-slate-700 border border-slate-700 px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0C5.37 0 0 5.373 0 12c0 5.303 3.438 9.8 8.205 11.387.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222 0 1.606-.015 2.896-.015 3.286 0 .322.216.694.825.576C20.565 21.795 24 17.298 24 12c0-6.627-5.373-12-12-12z" />
                </svg>
                GitHub Profile
              </a>
              <a
                href="https://www.linkedin.com/in/muhammad-ilyas-873a17178"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-sky-400 bg-sky-400/10 hover:bg-sky-400/20 border border-sky-400/30 px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
                LinkedIn
              </a>
            </div>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {CARDS.map(c => (
              <div
                key={c.label}
                className="bg-slate-800/50 border border-slate-700/60 hover:border-sky-500/40 rounded-xl p-4 sm:p-5 transition-all group hover:bg-slate-800"
              >
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-sky-500/10 border border-sky-500/20 text-sky-400 rounded-lg flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-sky-500 group-hover:text-white group-hover:border-sky-500 transition-all">
                  {c.icon}
                </div>
                <h4 className="text-slate-100 font-semibold mb-1 sm:mb-1.5 text-sm">{c.label}</h4>
                <p className="text-slate-500 text-xs leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}
